import {ActiveUserStore} from '@active-user-management';
import {inject, Injectable, Injector} from '@angular/core';
import {NavigationExtras, Router} from '@angular/router';
import {firstValueFrom} from 'rxjs';
import {AuthStore} from '../+store/auth.store';
import {RefreshLoginRequestModel} from '../models/login/refresh-login-request.model';
import {
	EnvironmentRedirectUrlParams,
	EnvironmentRedirectUrlWithAuthParams,
	isEnvironmentRedirectUrlParams,
	isEnvironmentRedirectUrlWithAuthParams,
	REDIRECTION_PAGE,
	RETURN_URL_PARAM,
	ValidEnvironmentUrlParams,
} from '../types/navigation-management.types';

/**
 * This service provides tools and functions for testing/verifying routing processes in the system, and in particular, in the verification/routing processes between system environments according to user settings.
 */

/**
 * # 🌐 Cross-Environment Redirect Flow
 *
 * This document describes the logic and flow for redirecting users between environments when they are authenticated in the wrong environment.
 *
 * ---
 *
 * ## 🔁 Redirect Scenarios
 *
 * ### ✅ Case 1: Login in the Wrong Environment
 * Occurs when a user logs in via `authStore.login()`, but the returned `apiUri` does **not** match the current environment.
 *
 * **Steps:**
 * 1. Detect mismatch between `apiUri` and current environment.
 * 2. Construct a redirect URL including all necessary parameters for a future `refreshToken` request.
 * 3. Redirect the user to:
 *    `https://X.doccycle.ENV/redirect?returnUrl=...&accessToken=...&refreshToken=...&company=...`
 *
 * ---
 *
 * ### ✅ Case 2: Redirect via `id` + `encodedUser`
 * This is a lighter redirect that includes only `id` and `encodedUser`, typically passed as a single `returnUrl` parameter.
 * No direct token information is included.
 *
 * ---
 *
 * ## 🔍 `/redirect` Route Logic
 *
 * This public route is responsible for analyzing parameters and completing the redirect/authentication flow.
 *
 * ### Possible Parameter Sets:
 *
 * #### 🟡 Scenario A:
 * - Only `returnUrl` is present → Indicates **Case 2**
 *
 * #### 🟢 Scenario B:
 * - `returnUrl + accessToken + refreshToken + company` are present → Indicates **Case 1**
 *
 * **Steps for Case 1:**
 * 1. If a user is already logged in → perform logout.
 * 2. Execute token refresh using query params.
 * 3. Navigate to `returnUrl` (or `/home` as fallback).
 *
 * ---
 *
 * ## 🧩 Files & Components Involved
 *
 * ### 🌱 In Source Environment:
 * - `LoginComponent` – Initial login entry point.
 * - `authStore.login()` – Triggers login request.
 * - `apiUri` – Used to determine environment mismatch.
 *
 * ### 🌍 In Target Environment:
 * - `/redirect` route:
 *   - Parses query parameters.
 *   - Refreshes tokens (if needed).
 *   - Redirects to the intended destination.
 *
 * ---
 *
 * ## 🧠 Notes
 *
 * - Token refresh must be handled **securely** using HTTPS.
 * - Avoid caching sensitive redirect URLs.
 * - Ensure logout logic clears local/session storage as needed.
 */

@Injectable({
	providedIn: 'root',
})
export class NavigationManagementService {
	/**
	 * 'returnUrl' is the parameter used to redirect the user inside the app. (after login)
	 */

	/**
	 * Patterns to identify environments that should not be redirected.
	 * @private
	 */
	public readonly NO_REDIRECT_PATTERNS: ReadonlyArray<RegExp> = [
		// localhost in host
		/localhost/,
		// ip	address in host
		/\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}/,
	];

	private readonly router: Router = inject(Router);
	private readonly activeUserStore = inject(ActiveUserStore);
	private injector = inject(Injector); // Injected Injector

	// Getter for AuthStore to resolve it lazily
	private get authStore(): AuthStore {
		return this.injector.get(AuthStore);
	}

	constructor() {}

	// Computed/Properties:
	//

	// Helpers:
	private log(...args: any[]) {
		console.debug(`[NavigationManagementService]`, ...args);
	}

	// - parseHost(url): {company?, env?}
	// {protocol}://{company}.doccycle.{env}/
	// {protocol}://doccycle.{env}/
	public parseHost(url: string): {company?: string; env?: string; isLocalhost?: boolean} {
		try {
			const host = new URL(url)?.host;
			if (!host) return {company: undefined, env: undefined};
			const hostParts = host.split('.');
			const company = hostParts[0] === 'doccycle' ? undefined : hostParts[0];
			const env = hostParts[hostParts.length - 1];
			const isLocalhost = url.indexOf('localhost') !== -1;
			return {company, env, isLocalhost};
		} catch (e) {
			this.log('parseHost(): Error parsing URL:', {url, error: e});
			return {company: undefined, env: undefined};
		}
	}

	// Methods:
	// Source Environment:
	// - validateEnvOrigin(apiUriFromConnectResponse)
	public validateEnvOrigin(targetApiUri: string): boolean {
		try {
			if (!targetApiUri || this.NO_REDIRECT_PATTERNS.some(pattern => pattern.test(window.location.host))) return true;
			const apiUriHost = new URL(targetApiUri).host;
			const currentHost = window.location.host;
			return apiUriHost === currentHost;
		} catch (e) {
			this.log('validateEnvOrigin(): Error validating environment origin:', {targetApiUri, error: e});
			return false;
		}
	}

	public generateRedirectUrl(targetHost: string, params: ValidEnvironmentUrlParams): URL {
		try {
			const url = new URL(targetHost + `/${REDIRECTION_PAGE}`);
			const searchParams = new URLSearchParams();
			Object.entries(params).forEach(([key, value]) => {
				if (value) searchParams.append(key, value); // ללא encodeURIComponent
			});
			url.search = searchParams.toString();
			return url;
		} catch (e) {
			this.log('generateRedirectUrl(): Error generating redirect URL:', {targetHost, params, error: e});
			return new URL(targetHost);
		}
	}

	public getCurrentRedirectUri(urlTo?: string): string | null {
		// NEW
		const currentUrl = new URL(window.location.href);
		const redirectUrl = currentUrl.searchParams.get(RETURN_URL_PARAM);
		// if the url contains the returnUrl param, return it
		if (redirectUrl) return redirectUrl;
		return urlTo || currentUrl.pathname || '';
	}

	// - redirectToEnvironment
	public async redirectToEnvironment(newUrl: URL) {
		// logout the active user if exists
		if (this.activeUserStore.user$()) {
			await this.authStore.logout(null, null);
		}
		// force redirect to the new URL (without saving the current URL in history)
		try {
			window.location.replace(newUrl);
			return Promise.resolve();
		} catch (error) {
			this.log('redirectToEnvironment(): Error redirecting to environment:', {newUrl, error});
			return Promise.reject(error);
		}
	}

	//////////////////////////// Target Environment: ////////////////////////
	public processRedirectUrlStructure(href: string = window.location.href) {
		try {
			const url = new URL(href);
			const searchParams = url.searchParams;
			const searchParamsObject = Object.fromEntries(searchParams.entries());
			if (isEnvironmentRedirectUrlWithAuthParams(searchParamsObject)) {
				// Case 1: redirect with auth params
				return this.connectAndRedirect(searchParamsObject);
			} else if (isEnvironmentRedirectUrlParams(searchParamsObject)) {
				return this.redirectInApp(searchParamsObject);
			}
			// Case 2: redirect with returnUrl only
			const returnUrl = searchParams.get(RETURN_URL_PARAM);
			return this.router.navigate([returnUrl || 'home']);
		} catch (e) {
			this.log('checkRedirectUrlStructure(): Error parsing URL:', {href, error: e});
			return null;
		}
	}

	// - connect(params) // disconnect the active user if exists and if it's different from the one in the params, then refresh tokens to ensure the user is authenticated in the target environment, call redirectToUrl
	public async connectAndRedirect(params: EnvironmentRedirectUrlWithAuthParams) {
		const {accessToken, refreshToken, company, redirectUri} = params;
		if (this.activeUserStore.user$()) {
			await this.authStore.logout(null, null);
		}
		const refreshLoginRequest = new RefreshLoginRequestModel({
			accessToken,
			refreshToken,
			company,
		});
		return firstValueFrom(this.authStore.refreshLogin(refreshLoginRequest, null, null, false))
			.then(response => {
				if (response) {
					this.log('connectAndRedirect(): Login successful, redirecting to:', redirectUri);
					// navigate to the redirectUri (or home as fallback)
					return this.redirectInApp({redirectUri});
				}
				this.log('connectAndRedirect(): Login failed, redirecting to login page');
				// handle error (e.g., show error message, redirect to login page, etc.)
				debugger;
				return this.navigateToLoginThenNavigateTo(redirectUri || 'home');
			})
			.catch(error => {
				this.log('connectAndRedirect(): Error in refreshLogin:', {error});
				// handle error (e.g., show error message, redirect to login page, etc.)
				return this.router.navigate(['login']);
			});
	}

	public async redirectInApp(params: EnvironmentRedirectUrlParams) {
		const {redirectUri} = params;
		if (redirectUri) {
			return this.router.navigate([redirectUri]);
		} else {
			return this.router.navigate(['home']);
		}
	}

	public navigateToLoginThenNavigateTo(urlAfterLogin: string) {
		const urlExtras: NavigationExtras = {};
		if (urlAfterLogin) {
			urlExtras.queryParams = {[RETURN_URL_PARAM]: urlAfterLogin};
		}
		return this.router.navigate(['login'], urlExtras);
	}

	// - redirectToUrl(url) // redirect to the URL using redirectUrl (or home for permanent users only)
}
